// Atributos y plantillas — Inventario → Catálogo → Atributos.
// Parametrización de variantes: define atributos (talla, color…), sus valores
// (con abreviatura para el SKU) y qué atributos aplican a cada categoría (familia).
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Plus, Tags, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { Campo, Switch, fieldCls, inv } from './shared';

const TIPOS = [
  { v: 'lista', label: 'Lista de valores' },
  { v: 'texto', label: 'Texto libre' },
  { v: 'numero', label: 'Numérico' },
  { v: 'booleano', label: 'Sí / No' },
];

interface Atributo { id: string; codigo: string | null; nombre: string; tipo: string; genera_variante: boolean; orden: number; es_activo: boolean }
interface ValorAtr { id: string; atributo_id: string; valor: string; abreviatura: string | null; orden: number; es_activo: boolean }
interface Categoria { id: string; nombre: string }
interface CatAtr { id: string; categoria_id: string; atributo_id: string; obligatorio: boolean }

const vacioAtr = { nombre: '', tipo: 'lista', genera_variante: true, orden: 0, es_activo: true };

export function Atributos() {
  const qc = useQueryClient();
  const [vista, setVista] = useState<'atributos' | 'plantillas'>('atributos');

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-3">
        <Tags className="w-7 h-7 text-primary" />
        <div>
          <h1 className="text-xl font-semibold">Atributos y plantillas</h1>
          <p className="text-sm text-muted-foreground">Parametrización de variantes (talla, color, …) y qué aplica a cada familia</p>
        </div>
      </div>
      <div className="flex gap-1 border-b">
        {([['atributos', 'Atributos y valores'], ['plantillas', 'Plantilla por categoría']] as const).map(([k, l]) => (
          <button key={k} onClick={() => setVista(k)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${vista === k ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>{l}</button>
        ))}
      </div>
      {vista === 'atributos' ? <TabAtributos qc={qc} /> : <TabPlantillas qc={qc} />}
    </div>
  );
}

// ── Tab 1: Atributos (maestro) + Valores (detalle) ────────────────────────────
function TabAtributos({ qc }: { qc: ReturnType<typeof useQueryClient> }) {
  const dlg = useDialogoEnLinea();
  const [sel, setSel] = useState<string | null>(null);
  const [form, setForm] = useState<typeof vacioAtr>(vacioAtr);
  const [editId, setEditId] = useState<string | null>(null);

  const { data: atributos = [], isFetching } = useQuery<Atributo[]>({
    queryKey: ['inv-atributos'], queryFn: () => inv.list<Atributo>('atributos', '?order=orden,nombre'),
  });

  const guardar = useMutation({
    mutationFn: () => {
      const body = { nombre: form.nombre, tipo: form.tipo, genera_variante: form.genera_variante, orden: Number(form.orden) || 0, es_activo: form.es_activo, updated_at: new Date().toISOString() };
      return editId ? inv.update('atributos', editId, body) : inv.create('atributos', body);
    },
    onSuccess: () => { toast.success('Atributo guardado'); setForm(vacioAtr); setEditId(null); void qc.invalidateQueries({ queryKey: ['inv-atributos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => inv.remove('atributos', id),
    onSuccess: (_d, id) => { toast.success('Atributo eliminado'); if (sel === id) setSel(null); void qc.invalidateQueries({ queryKey: ['inv-atributos'] }); },
    onError: () => toast.error('No se puede eliminar: está en uso por productos o categorías'),
  });

  const editar = (a: Atributo) => { setForm({ nombre: a.nombre, tipo: a.tipo, genera_variante: a.genera_variante, orden: a.orden, es_activo: a.es_activo }); setEditId(a.id); };
  const seleccionado = atributos.find((a) => a.id === sel) || null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {dlg.ui}
      {/* Atributos */}
      <div className="space-y-3">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">{editId ? 'Editar atributo' : 'Nuevo atributo'}</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-2 gap-3">
            <Campo label="Nombre *" className="col-span-2"><input className={fieldCls} value={form.nombre} placeholder="Talla, Color, Material…" onChange={(e) => setForm({ ...form, nombre: e.target.value })} /></Campo>
            <Campo label="Tipo"><select className={fieldCls} value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}>{TIPOS.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}</select></Campo>
            <Campo label="Orden"><input type="number" className={fieldCls} value={form.orden} onChange={(e) => setForm({ ...form, orden: Number(e.target.value) })} /></Campo>
            <div className="col-span-2 flex items-center justify-between">
              <div className="flex items-center gap-6">
                <Switch checked={form.genera_variante} onChange={(v) => setForm({ ...form, genera_variante: v })} label="Genera variante (SKU)" />
                <Switch checked={form.es_activo} onChange={(v) => setForm({ ...form, es_activo: v })} label="Activo" />
              </div>
              <div className="flex gap-2">
                {editId && <Button variant="outline" size="sm" onClick={() => { setForm(vacioAtr); setEditId(null); }}>Cancelar</Button>}
                <Button size="sm" isLoading={guardar.isPending} disabled={!form.nombre.trim()} onClick={() => guardar.mutate()}><Plus className="w-4 h-4 mr-1" />{editId ? 'Guardar' : 'Agregar'}</Button>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 overflow-x-auto">
            {isFetching && <div className="py-4 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-primary" /></div>}
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-2 pr-3">Atributo</th><th className="py-2 pr-3">Tipo</th><th className="py-2 pr-3">Variante</th><th className="py-2"></th></tr></thead>
              <tbody>
                {atributos.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-muted-foreground">Sin atributos</td></tr>}
                {atributos.map((a) => (
                  <tr key={a.id} className={`border-b last:border-0 cursor-pointer ${sel === a.id ? 'bg-primary/10' : 'hover:bg-muted/40'}`} onClick={() => setSel(a.id)}>
                    <td className="py-2 pr-3 font-medium">{a.nombre}{!a.es_activo && <span className="ml-1 text-xs text-muted-foreground">(inactivo)</span>}</td>
                    <td className="py-2 pr-3 text-muted-foreground">{TIPOS.find((t) => t.v === a.tipo)?.label || a.tipo}</td>
                    <td className="py-2 pr-3">{a.genera_variante ? 'Sí' : '—'}</td>
                    <td className="py-2 text-right whitespace-nowrap">
                      <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={(e) => { e.stopPropagation(); editar(a); }}><Pencil className="w-4 h-4" /></button>
                      <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1" title="Eliminar" onClick={async (e) => { e.stopPropagation(); if (await dlg.confirmar(`¿Eliminar atributo "${a.nombre}"?`, { peligro: true })) eliminar.mutate(a.id); }}><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>

      {/* Valores del atributo seleccionado */}
      <div>
        {seleccionado ? (
          seleccionado.tipo === 'lista'
            ? <Valores qc={qc} atributo={seleccionado} />
            : <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">El atributo <b>{seleccionado.nombre}</b> es de tipo “{TIPOS.find((t) => t.v === seleccionado.tipo)?.label}”, no usa lista de valores.</CardContent></Card>
        ) : <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">Selecciona un atributo para gestionar sus valores.</CardContent></Card>}
      </div>
    </div>
  );
}

function Valores({ qc, atributo }: { qc: ReturnType<typeof useQueryClient>; atributo: Atributo }) {
  const dlg = useDialogoEnLinea();
  const [nuevo, setNuevo] = useState({ valor: '', abreviatura: '' });
  const { data: valores = [], isFetching } = useQuery<ValorAtr[]>({
    queryKey: ['inv-atr-valores', atributo.id], queryFn: () => inv.list<ValorAtr>('atributo_valores', `?atributo_id=eq.${atributo.id}&order=orden,valor`),
  });
  const refrescar = () => qc.invalidateQueries({ queryKey: ['inv-atr-valores', atributo.id] });
  const agregar = useMutation({
    mutationFn: () => inv.create('atributo_valores', { atributo_id: atributo.id, valor: nuevo.valor, abreviatura: nuevo.abreviatura || null, orden: valores.length }),
    onSuccess: () => { setNuevo({ valor: '', abreviatura: '' }); void refrescar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const actualizar = useMutation({
    mutationFn: (v: ValorAtr) => inv.update('atributo_valores', v.id, { valor: v.valor, abreviatura: v.abreviatura || null, orden: v.orden, es_activo: v.es_activo }),
    onSuccess: () => void refrescar(),
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => inv.remove('atributo_valores', id),
    onSuccess: () => void refrescar(),
    onError: () => toast.error('No se puede eliminar: el valor está en uso por una variante'),
  });

  return (
    <>
      {dlg.ui}
      <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">Valores de “{atributo.nombre}”</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-end gap-2">
          <Campo label="Valor" className="flex-1"><input className={fieldCls} value={nuevo.valor} placeholder="Rojo, S, 32…" onChange={(e) => setNuevo({ ...nuevo, valor: e.target.value })} onKeyDown={(e) => { if (e.key === 'Enter' && nuevo.valor.trim()) agregar.mutate(); }} /></Campo>
          <Campo label="Abrev. (SKU)" className="w-28"><input className={fieldCls} value={nuevo.abreviatura} placeholder="ROJ, S" onChange={(e) => setNuevo({ ...nuevo, abreviatura: e.target.value })} /></Campo>
          <Button size="sm" isLoading={agregar.isPending} disabled={!nuevo.valor.trim()} onClick={() => agregar.mutate()}><Plus className="w-4 h-4" /></Button>
        </div>
        {isFetching && <div className="py-2 text-center"><Loader2 className="w-4 h-4 animate-spin mx-auto text-primary" /></div>}
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-2 pr-2 w-10">#</th><th className="py-2 pr-2">Valor</th><th className="py-2 pr-2 w-28">Abrev.</th><th className="py-2"></th></tr></thead>
          <tbody>
            {valores.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-muted-foreground">Sin valores — agrega arriba</td></tr>}
            {valores.map((v, i) => (
              <tr key={v.id} className="border-b last:border-0">
                <td className="py-1 pr-2 text-muted-foreground">{i + 1}</td>
                <td className="py-1 pr-2"><input className={`${fieldCls} h-8`} defaultValue={v.valor} onBlur={(e) => { if (e.target.value !== v.valor && e.target.value.trim()) actualizar.mutate({ ...v, valor: e.target.value }); }} /></td>
                <td className="py-1 pr-2"><input className={`${fieldCls} h-8`} defaultValue={v.abreviatura ?? ''} onBlur={(e) => { if ((e.target.value || null) !== (v.abreviatura ?? null)) actualizar.mutate({ ...v, abreviatura: e.target.value }); }} /></td>
                <td className="py-1 text-right"><button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20" title="Eliminar" onClick={async () => { if (await dlg.confirmar(`¿Eliminar valor "${v.valor}"?`, { peligro: true })) eliminar.mutate(v.id); }}><Trash2 className="w-4 h-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
      </Card>
    </>
  );
}

// ── Tab 2: Plantilla por categoría (qué atributos aplican) ────────────────────
function TabPlantillas({ qc }: { qc: ReturnType<typeof useQueryClient> }) {
  const [cat, setCat] = useState('');
  const { data: categorias = [] } = useQuery<Categoria[]>({ queryKey: ['inv-cats-attr'], queryFn: () => inv.list<Categoria>('categorias', '?select=id,nombre&order=nombre') });
  const { data: atributos = [] } = useQuery<Atributo[]>({ queryKey: ['inv-atributos'], queryFn: () => inv.list<Atributo>('atributos', '?order=orden,nombre') });
  const { data: asignados = [] } = useQuery<CatAtr[]>({
    queryKey: ['inv-cat-attr', cat], queryFn: () => inv.list<CatAtr>('categoria_atributos', `?categoria_id=eq.${cat}`), enabled: !!cat,
  });
  const refrescar = () => qc.invalidateQueries({ queryKey: ['inv-cat-attr', cat] });
  const mapa = new Map(asignados.map((a) => [a.atributo_id, a]));

  const toggle = useMutation({
    mutationFn: async (a: Atributo) => {
      const ex = mapa.get(a.id);
      if (ex) return inv.remove('categoria_atributos', ex.id);
      return inv.create('categoria_atributos', { categoria_id: cat, atributo_id: a.id, obligatorio: false, orden: a.orden });
    },
    onSuccess: () => void refrescar(),
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const setObligatorio = useMutation({
    mutationFn: (p: { id: string; obligatorio: boolean }) => inv.update('categoria_atributos', p.id, { obligatorio: p.obligatorio }),
    onSuccess: () => void refrescar(),
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">Atributos por categoría (familia)</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <Campo label="Categoría" className="max-w-md">
          <select className={fieldCls} value={cat} onChange={(e) => setCat(e.target.value)}>
            <option value="">— Selecciona una categoría —</option>
            {categorias.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </select>
        </Campo>
        {!cat ? (
          <p className="py-8 text-center text-sm text-muted-foreground">Elige una categoría para definir qué atributos aplican a sus productos.</p>
        ) : atributos.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">Primero crea atributos en la pestaña “Atributos y valores”.</p>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-2 pr-3 w-10">Aplica</th><th className="py-2 pr-3">Atributo</th><th className="py-2 pr-3">Tipo</th><th className="py-2 pr-3">Obligatorio</th></tr></thead>
            <tbody>
              {atributos.map((a) => {
                const asg = mapa.get(a.id);
                return (
                  <tr key={a.id} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="py-2 pr-3"><input type="checkbox" className="h-4 w-4 rounded border-input" checked={!!asg} onChange={() => toggle.mutate(a)} /></td>
                    <td className="py-2 pr-3 font-medium">{a.nombre}</td>
                    <td className="py-2 pr-3 text-muted-foreground">{TIPOS.find((t) => t.v === a.tipo)?.label || a.tipo}</td>
                    <td className="py-2 pr-3">{asg ? <input type="checkbox" className="h-4 w-4 rounded border-input" checked={asg.obligatorio} onChange={(e) => setObligatorio.mutate({ id: asg.id, obligatorio: e.target.checked })} /> : <span className="text-xs text-muted-foreground">—</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}
